// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Engine snapshot cards + aircraft snapshot + engine-delta overview card
// builders, extracted from app.js. These return HTML strings consumed by
// the multi-engine overview render path. They share the flightData /
// currentEngineDescriptors / healthSummary state with the rest of the app
// so they're exposed as a factory.

import { averageValues } from "../core/math.js";
import { escapeHtml } from "../core/html.js";
import { oilSystemParameters } from "../analysis/detectors/oil-system.js";

export function createEngineSnapshot({
  getFlightData,
  getHealthSummary,
  scopedSummaryForEngine,
  currentEngineDescriptors,
  engineMetricValue,
  engineDelta,
  engineDeltaLabel,
  finiteValues,
  extentFromValues,
  panelNumber,
  signedPanelNumber,
  formatElapsedTime,
  flightDurationSeconds,
  fuelUsedMetric,
}) {
  function aircraftFuelFlowAverage() {
    const flightData = getFlightData();
    const aggregateAverage = averageValues(finiteValues(flightData, "fuelFlow"));
    if (Number.isFinite(aggregateAverage)) return aggregateAverage;
    const engines = currentEngineDescriptors();
    return averageValues(flightData.map((point) => {
      const values = engines.map((engine) => engineMetricValue(point, engine, "fuelFlow")).filter(Number.isFinite);
      return values.length ? values.reduce((total, value) => total + value, 0) : null;
    }));
  }

  function aircraftSnapshotCard(summary, options = {}) {
    const score = summary?.overall?.score ?? "--";
    const label = summary?.overall?.label ?? "Unknown";
    const className = summary?.overall?.className ?? "muted";
    const aircraftRows = aircraftSnapshotRows();
    return `
    <article class="multi-engine-card is-aircraft ${options.active ? "is-active" : ""}" data-engine-scope-card="aircraft" role="button" tabindex="0" aria-pressed="${options.active ? "true" : "false"}">
      <header>
        <span>AC</span>
        <div>
          <p>Aircraft</p>
          <strong class="${className}">${score}/100 ${escapeHtml(label)}</strong>
        </div>
      </header>
      <dl>
        ${aircraftRows.map((row) => `<div><dt>${escapeHtml(row.label)}</dt><dd>${escapeHtml(row.value)}</dd></div>`).join("")}
      </dl>
    </article>
  `;
  }

  function aircraftSnapshotRows() {
    const flightData = getFlightData();
    const engines = currentEngineDescriptors();
    if (engines.length > 1) {
      const [left, right] = engines;
      const deltaLabel = engineDeltaLabel(left, right);
      return [
        { label: "Fuel avg", value: panelNumber(aircraftFuelFlowAverage(), " GPH", 1) },
        { label: "Bus", value: panelNumber(averageValues(finiteValues(flightData, "buss")), " V", 1) },
        { label: `Fuel ${deltaLabel}`, value: signedPanelNumber(averageEngineDelta(left, right, "fuelFlow"), " GPH", 1) },
        { label: "CHT max delta", value: panelNumber(peakEngineDelta(left, right, "cht"), " F") },
        { label: `Oil P ${deltaLabel}`, value: signedPanelNumber(averageEngineDelta(left, right, "oilPressure"), " psi") },
        { label: `Oil T ${deltaLabel}`, value: signedPanelNumber(averageEngineDelta(left, right, "oilTemp"), " F") },
      ];
    }
    return [
      { label: "Fuel avg", value: panelNumber(aircraftFuelFlowAverage(), " GPH", 1) },
      { label: "Bus", value: panelNumber(averageValues(finiteValues(flightData, "buss")), " V", 1) },
      { label: "Duration", value: formatElapsedTime(flightDurationSeconds()) },
      { label: "Fuel used", value: fuelUsedMetric() },
    ];
  }

  function engineMetricOverviewFloor(metric) {
    return {
      fuelFlow: 0.05,
      rpm: 300,
      map: 1,
      oilPressure: 1,
    }[metric] ?? null;
  }

  function finiteMetricValuesForEngine(engine, metric, options = {}) {
    if (options.ignoreNonRunning && metric === "oilPressure") {
      const operatingValues = operatingOilPressureValuesForEngine(engine);
      if (operatingValues.length >= oilSystemParameters.minRunningRows) return operatingValues;
    }
    const floor = options.ignoreNonRunning ? engineMetricOverviewFloor(metric) : null;
    return getFlightData()
      .map((point) => engineMetricValue(point, engine, metric))
      .filter((value) => Number.isFinite(value) && (!Number.isFinite(floor) || value > floor));
  }

  function overviewOilPressureRunningPoint(point, engine) {
    const phase = String(point?.phase ?? "");
    if (["Startup", "Shutdown"].includes(phase)) return false;
    const rpm = engineMetricValue(point, engine, "rpm");
    if (!Number.isFinite(rpm) || rpm <= oilSystemParameters.runningRpm) return false;
    if (oilSystemParameters.flightPowerPhases.includes(phase)) return true;
    const percentPower = Number(point?.percentPower);
    if (Number.isFinite(percentPower)) return percentPower >= oilSystemParameters.groundConcernPercentPower;
    const fuelFlow = engineMetricValue(point, engine, "fuelFlow");
    if (Number.isFinite(fuelFlow) && fuelFlow >= oilSystemParameters.groundConcernFuelFlowGph) return true;
    return rpm >= oilSystemParameters.groundConcernRpm;
  }

  function operatingOilPressureValuesForEngine(engine) {
    return getFlightData()
      .filter((point) => overviewOilPressureRunningPoint(point, engine))
      .map((point) => engineMetricValue(point, engine, "oilPressure"))
      .filter(Number.isFinite);
  }

  function averageMetricForEngine(engine, metric, options = {}) {
    return averageValues(finiteMetricValuesForEngine(engine, metric, options));
  }

  function extentMetricForEngine(engine, metric, options = {}) {
    return extentFromValues(finiteMetricValuesForEngine(engine, metric, options));
  }

  function engineSnapshotStatus(engine) {
    // Derive the card status from the same per-engine composite score the
    // gauge shows, so the overview cards and the per-engine view never
    // disagree. The scoped summary already filters non-running transients
    // that a raw CHT/oil-limit check on the card would trip on.
    const overall = scopedSummaryForEngine(getHealthSummary(), engine)?.overall;
    return { label: overall?.label ?? "Unknown", className: overall?.className ?? "muted" };
  }

  function engineSnapshotCard(engine, options = {}) {
    const status = engineSnapshotStatus(engine);
    const peakCht = extentMetricForEngine(engine, "cht")?.max ?? null;
    const avgFuel = averageMetricForEngine(engine, "fuelFlow", { ignoreNonRunning: true });
    const avgRpm = averageMetricForEngine(engine, "rpm", { ignoreNonRunning: true });
    const oilMin = extentMetricForEngine(engine, "oilPressure", { ignoreNonRunning: true })?.min ?? null;
    return `
    <article class="multi-engine-card ${options.active ? "is-active" : ""}" data-engine-id="${escapeHtml(engine.id)}" data-engine-scope-card="${escapeHtml(engine.id)}" role="button" tabindex="0" aria-pressed="${options.active ? "true" : "false"}">
      <header>
        <span>${escapeHtml(engine.shortLabel)}</span>
        <div>
          <p>${escapeHtml(engine.label)}</p>
          <strong class="${status.className}">${escapeHtml(status.label)}</strong>
        </div>
      </header>
      <dl>
        <div><dt>Peak CHT</dt><dd>${panelNumber(peakCht, " F")}</dd></div>
        <div><dt>Fuel avg</dt><dd>${panelNumber(avgFuel, " GPH", 1)}</dd></div>
        <div><dt>RPM avg</dt><dd>${panelNumber(avgRpm)}</dd></div>
        <div><dt>Oil P min</dt><dd>${panelNumber(oilMin, " psi")}</dd></div>
      </dl>
    </article>
  `;
  }

  function averageEngineDelta(left, right, metric) {
    if (getHealthSummary()?.engineDelta?.details?.reason === "unverified-source-timing") return null;
    const floor = engineMetricOverviewFloor(metric);
    const values = getFlightData()
      .map((point) => {
        const leftValue = engineMetricValue(point, left, metric);
        const rightValue = engineMetricValue(point, right, metric);
        if (Number.isFinite(floor) && (!(leftValue > floor) || !(rightValue > floor))) return null;
        return Number.isFinite(leftValue) && Number.isFinite(rightValue) ? rightValue - leftValue : null;
      })
      .filter(Number.isFinite);
    return averageValues(values);
  }

  function peakEngineDelta(left, right, metric) {
    if (getHealthSummary()?.engineDelta?.details?.reason === "unverified-source-timing") return null;
    const values = getFlightData()
      .map((point) => engineDelta(point, left, right, metric))
      .filter(Number.isFinite)
      .map(Math.abs);
    return extentFromValues(values)?.max ?? null;
  }

  function engineDeltaOverviewCard(left, right) {
    const rpmDelta = averageEngineDelta(left, right, "rpm");
    const mapDelta = averageEngineDelta(left, right, "map");
    const fuelDelta = averageEngineDelta(left, right, "fuelFlow");
    const chtDelta = peakEngineDelta(left, right, "cht");
    const oilDelta = averageEngineDelta(left, right, "oilPressure");
    const comparisonMetric = getHealthSummary()?.engineDelta;
    const review = [
      Math.abs(rpmDelta ?? 0) >= 80,
      Math.abs(mapDelta ?? 0) >= 1.5,
      Math.abs(fuelDelta ?? 0) >= 1.5,
      Math.abs(oilDelta ?? 0) >= 10,
      (chtDelta ?? 0) >= 25,
    ].some(Boolean);
    const unverified = comparisonMetric?.details?.reason === "unverified-source-timing";
    // Owner-set engine timing (Sync engine data) still compares, but says so.
    const manualTiming = comparisonMetric?.details?.timing === "manual";
    const verdict = Number.isFinite(comparisonMetric?.score)
      ? comparisonMetric.details?.status ?? (comparisonMetric.score < 88 ? "Compare" : "Aligned")
      : review ? "Compare" : "Aligned";
    const statusLabel = unverified ? "Timing not verified" : manualTiming ? `${verdict}, manual timing` : verdict;
    const statusClass = unverified ? "muted" : Number.isFinite(comparisonMetric?.score)
      ? comparisonMetric.score < 72 ? "red" : comparisonMetric.score < 88 ? "amber" : "green"
      : review ? "amber" : "green";
    const deltaLabel = engineDeltaLabel(left, right);
    return `
    <article class="multi-engine-card is-delta">
      <header>
        <span>Δ</span>
        <div>
          <p>Engine Delta</p>
          <strong class="${statusClass}">${escapeHtml(statusLabel)}</strong>
        </div>
      </header>
      <dl>
        <div><dt>RPM ${escapeHtml(deltaLabel)}</dt><dd>${signedPanelNumber(rpmDelta)}</dd></div>
        <div><dt>MAP ${escapeHtml(deltaLabel)}</dt><dd>${signedPanelNumber(mapDelta, " inHg", 1)}</dd></div>
        <div><dt>Fuel ${escapeHtml(deltaLabel)}</dt><dd>${signedPanelNumber(fuelDelta, " GPH", 1)}</dd></div>
        <div><dt>CHT max Δ</dt><dd>${panelNumber(chtDelta, " F")}</dd></div>
      </dl>
    </article>
  `;
  }

  return {
    aircraftFuelFlowAverage,
    aircraftSnapshotCard,
    aircraftSnapshotRows,
    engineMetricOverviewFloor,
    finiteMetricValuesForEngine,
    operatingOilPressureValuesForEngine,
    averageMetricForEngine,
    extentMetricForEngine,
    engineSnapshotStatus,
    engineSnapshotCard,
    averageEngineDelta,
    peakEngineDelta,
    engineDeltaOverviewCard,
  };
}
